import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bookmark, RefreshCw } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { TxTitle, useArticlePath } from "@/lib/i18n";
import { SECTOR_LABELS } from "@/components/layernav/navConfig";
import { useAdRotation, AdLink, AdMedia } from "@/components/AdSlot";
import { homeTrack } from "../shared";

const FALLBACK = "/thumbs/market_globe.jpg";

export const CAT_COLORS: Record<string, string> = {
  crypto: "#7C9BFF", markets: "#22D3EE", defi: "#A78BFA", web3: "#8B5CF6",
  blockchain: "#3A8DFF", onchain: "#3A8DFF", "government-policy": "#C084FC",
  ai: "#34D399", quantum: "#67E8F9", research: "#F59E0B", explainers: "#94A3B8",
  security: "#F87171", business: "#F59E0B", ventures: "#F59E0B",
};

export const catLabel = (slug?: string) =>
  !slug ? "News" : SECTOR_LABELS[slug] || (slug === "markets" ? "Markets" : slug === "government-policy" ? "Policy" : slug);

const isLive = (a: Article) =>
  !!(a as any).is_breaking && Date.now() - new Date(a.published_at).getTime() < 24 * 3.6e6;

const LiveBadge = () => (
  <span data-testid="feed-live-badge"
    className="inline-flex items-center gap-1 rounded-full border border-[#7C3AED]/45 bg-[#7C3AED]/15 px-1.5 py-[1px] font-mono text-[8px] font-bold uppercase tracking-widest text-[#C4B5FD]">
    <span aria-hidden className="h-1 w-1 rounded-full bg-[#A78BFA] motion-safe:animate-pulse" /> Live
  </span>
);

const BookmarkButton = ({ a }: { a: Article }) => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const saved = !!user?.saved_articles?.includes(a.id);
  const toggle = async () => {
    if (!user) { toast.error("Sign in to save stories"); navigate("/login"); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (saved) await api.delete(`/account/saved/${a.id}`);
      else await api.post(`/account/saved/${a.id}`);
      await refresh();
      homeTrack("more_stories", saved ? "bookmark_remove" : "bookmark_add");
      toast.success(saved ? "Removed from saved" : "Saved to your account");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={toggle} disabled={busy}
      aria-label={saved ? `Remove "${a.title}" from saved stories` : `Save "${a.title}"`}
      data-testid={`feed-bookmark-${a.slug}`}
      className={`absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/60 motion-safe:transition-colors ${saved ? "text-[#A78BFA]" : "text-gray-600 hover:text-[#A78BFA]"}`}>
      <Bookmark className={`h-4 w-4 ${saved ? "fill-[#A78BFA]" : ""}`} aria-hidden />
    </button>
  );
};

const FeedRow = ({ a }: { a: Article }) => {
  const toArticle = useArticlePath();
  const live = isLive(a);
  const cat = a.categories?.[0];
  const color = CAT_COLORS[cat || ""] || "#8b93a7";
  return (
    <li className={`relative list-none ${live
      ? "rounded-[10px] border border-[#7C3AED]/[0.25] bg-[#7C3AED]/[0.06] shadow-[0_0_26px_-12px_rgba(124,58,237,0.45)] my-1.5"
      : "border-b border-white/[0.05] last:border-0"}`}
      data-testid={`feed-row-${a.slug}`}>
      <Link to={toArticle(a.slug)} onClick={() => homeTrack("more_stories", a.slug)}
        className="group flex items-center gap-3.5 sm:gap-4 px-2.5 sm:px-4 py-3 pr-11 sm:pr-12 rounded-[10px] hover:bg-white/[0.025] motion-safe:transition-colors motion-safe:duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/60">
        <img src={a.hero_image_url || FALLBACK} alt="" loading="lazy" width={110} height={64}
          onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
          className="w-[96px] h-[56px] sm:w-[110px] sm:h-[64px] object-cover rounded-md shrink-0 border border-white/[0.06] motion-safe:transition-transform motion-safe:duration-200 group-hover:scale-[1.01]" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 font-mono text-[9px] sm:text-[9.5px] uppercase tracking-[0.16em]">
            <span style={{ color }}>{catLabel(cat)}</span>
            <span className="text-gray-700" aria-hidden>·</span>
            <span className="text-gray-500">{timeAgo(a.published_at)}</span>
            {live && <LiveBadge />}
          </span>
          <span className="block font-news text-[14.5px] sm:text-[16.5px] font-semibold text-gray-100 group-hover:text-white leading-snug line-clamp-2 mt-1.5 motion-safe:transition-colors">
            <TxTitle slug={a.slug} title={a.title} />
          </span>
        </span>
      </Link>
      <BookmarkButton a={a} />
    </li>
  );
};

const SponsoredFeedRow = () => {
  const { ad, hidden } = useAdRotation("in_feed");
  if (hidden || !ad) return null;
  return (
    <li className="relative list-none border-t border-white/[0.05]" data-testid="feed-sponsored-row">
      <AdLink ad={ad} testid="feed-sponsored-link"
        className="group flex items-center gap-3.5 sm:gap-4 px-2.5 sm:px-4 py-3 hover:bg-white/[0.025] motion-safe:transition-colors">
        <AdMedia ad={ad} className="w-[96px] h-[56px] sm:w-[110px] sm:h-[64px] object-cover rounded-md shrink-0 border border-[#F59E0B]/40" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 font-mono text-[9px] sm:text-[9.5px] uppercase tracking-[0.16em]">
            <span className="text-[#FBBF24]">Sponsored</span>
            <span className="text-gray-700" aria-hidden>·</span>
            <span className="text-gray-500 truncate">{ad.advertiser}</span>
          </span>
          <span className="block font-news text-[14.5px] sm:text-[16.5px] font-semibold text-gray-200 group-hover:text-white leading-snug line-clamp-2 mt-1.5 motion-safe:transition-colors">
            {ad.name}
          </span>
        </span>
        <span className="font-mono text-[9px] text-gray-500 uppercase tracking-widest border border-white/15 rounded px-1.5 py-0.5 shrink-0">Ad</span>
      </AdLink>
    </li>
  );
};

const SkeletonRow = () => (
  <li className="flex items-center gap-4 px-2.5 sm:px-4 py-3 border-b border-white/[0.05] last:border-0 list-none" aria-hidden>
    <span className="w-[96px] h-[56px] sm:w-[110px] sm:h-[64px] rounded-md bg-white/[0.04] animate-pulse shrink-0" />
    <span className="flex-1 min-w-0 space-y-2.5">
      <span className="block h-2 w-28 rounded bg-white/[0.05] animate-pulse" />
      <span className="block h-3.5 w-4/5 rounded bg-white/[0.06] animate-pulse" />
    </span>
  </li>
);

export const MoreStoriesLiveFeed = ({ initial, excludeSlugs }: {
  initial: Article[]; excludeSlugs: string[];
}) => {
  const { data, isError, refetch } = useQuery({
    queryKey: ["morestories-live"],
    queryFn: async () => (await api.get("/articles", { params: { limit: 30 } })).data,
    refetchInterval: 90_000,
    staleTime: 60_000,
  });
  const exclude = useMemo(() => new Set(excludeSlugs), [excludeSlugs]);
  const fresh: Article[] | null = useMemo(() => {
    if (!data?.items) return null;
    const items: Article[] = data.items;
    const upper = new Set(items.slice(0, 6).map((a) => a.slug));
    return items
      .filter((a) => !upper.has(a.slug) && !exclude.has(a.slug)
        && !["pr", "market_brief"].includes((a as any).content_type))
      .slice(0, 6);
  }, [data, exclude]);

  const [displayed, setDisplayed] = useState<Article[] | null>(null);
  const [pending, setPending] = useState<Article[] | null>(null);
  const first = useRef(true);
  useEffect(() => {
    if (!fresh) return;
    if (first.current || !displayed) { first.current = false; setDisplayed(fresh); return; }
    const cur = new Set(displayed.map((a) => a.slug));
    if (fresh.some((a) => !cur.has(a.slug))) setPending(fresh);
  }, [fresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = displayed ?? initial.slice(0, 6);
  const newCount = pending && displayed
    ? pending.filter((a) => !displayed.some((d) => d.slug === a.slug)).length : 0;
  const showPending = () => {
    if (pending) setDisplayed(pending);
    setPending(null);
    homeTrack("more_stories", "show_new_stories");
  };

  return (
    <div className="bg-black border border-white/[0.08] rounded-xl min-w-0 overflow-hidden flex flex-col" data-testid="live-newsstream">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-white/[0.07]">
        <p className="flex items-center gap-2 font-mono text-[10.5px] font-bold uppercase tracking-[0.18em] text-gray-200">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#7C3AED]" /> Live Newsstream
        </p>
        <p className="flex items-center gap-1.5 font-mono text-[9px] sm:text-[9.5px] uppercase tracking-[0.14em] text-[#27e7a6]"
          data-testid="feed-live-indicator">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#27e7a6] motion-safe:animate-pulse" />
          Updating live
        </p>
      </div>
      {newCount > 0 && (
        <div className="px-3 sm:px-4 pt-3">
          <button type="button" onClick={showPending} data-testid="feed-new-stories-pill"
            className="nl-fade-up inline-flex items-center gap-2 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-[#A78BFA] hover:bg-[#7C3AED]/20 motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/60">
            {newCount} new {newCount === 1 ? "story" : "stories"} — show
          </button>
        </div>
      )}
      <ul className="px-1 sm:px-1.5 py-1.5 flex-1" data-testid="feed-rows">
        {rows.map((a) => <FeedRow key={a.slug} a={a} />)}
        {!rows.length && !isError && [0, 1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} />)}
        {rows.length > 0 && <SponsoredFeedRow />}
      </ul>
      {isError && !rows.length && (
        <div className="px-4 py-8 text-center" data-testid="feed-error-state">
          <p className="text-[13px] text-gray-500">The newsstream is briefly unavailable.</p>
          <button type="button" onClick={() => refetch()}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-300 hover:bg-white/5 transition-colors">
            <RefreshCw className="h-3 w-3" aria-hidden /> Retry
          </button>
        </div>
      )}
    </div>
  );
};
